import React, { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Grid,
  Tabs,
  Tab,
  TextField,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MainLayout from '../../../../../../components/Layout/MainLayout';
import httpClient from '../../../../../../utils/httpClient';
import 'flag-icons/css/flag-icons.min.css';
import { useAuth, UserLevel } from '../../../../../../contexts/AuthContext';
import { getCountryList } from '../../../../../../utils/getCountryList';

interface BillingData {
  customerGuid: string;
  customerBillingGuid: string;
  addressBillingCountryCode: string;
  addressBillingState: string;
  addressBillingCity: string;
  addressBillingLine1: string;
  addressBillingLine2: string;
  addressBillingPostalCode: string;
  currencyBilling?: string;
  defaultPaymentMethodIdStripe?: string;
}

interface AvailablePlan {
  priceId: string;
  priceValueNormalized: string;
  priceValueNormalizedAsComplementAprox?: string;
  currencyAsComplement?: string;
  dateCotation?: string;
  isProductionPlan?: boolean;
  plansSubscription: number;
  name: string;
  description: string;
  commercialName: string;
  currency: string;
  paymentFrequence: string;
  trialConfigurations?: {
    enableTrial: boolean;
    trialDays: number;
    trialEndDateFormatted?: string | null;
  };
  paymentFlow?: {
    guid: string;
    statusCheckout: string;
    date: string;
  } | null;
  activeSubscription?: {
    subscriptionIdStripe: string;
    status: string;
    cancelAtPeriodEnd: boolean;
  } | null;
}

interface CheckoutPayload {
  priceId: string;
}

interface CheckoutResponse {
  urlRedirectCheckout: string;
  isSuccesToRedirectToCheckout: boolean;
  shouldRedirectToEnrollment: boolean;
}

interface PaymentHistoryItem {
  invoiceIdStripe: string;
  invoiceNumber?: string | null;
  dueDate?: string | null;
  paymentDate?: string | null;
  status: string;
  plan: string;
  invoiceDownloadUrl?: string | null;
  invoicePayUrl?: string | null;
}

interface ApiError {
  propertyName: string;
  errorMessage: string;
  errorCode: string;
  statusCode: number;
}

interface ApiResponse<T> {
  success: boolean;
  status: number;
  data?: T;
  errors?: ApiError[];
}

interface WarningMessage {
  text: string;
  link?: string;
  linkText?: string;
  action?: () => void;
}

const SubscriptionPlan: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tabIndex, setTabIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  const [errors, setErrors] = useState<ApiError[]>([]);
  const [successMessage, setSuccessMessage] = useState('');
  const [warningMessage, setWarningMessage] = useState<WarningMessage | null>(null);
  const [availablePlans, setAvailablePlans] = useState<AvailablePlan[]>([]);
  const [paymentHistoryItems, setPaymentHistoryItems] = useState<PaymentHistoryItem[]>([]);
  const [paymentHistoryLoading, setPaymentHistoryLoading] = useState(false);
  const [paymentHistoryHasError, setPaymentHistoryHasError] = useState(false);
  const [invoiceDownloadingId, setInvoiceDownloadingId] = useState<string | null>(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelingSubscription, setCancelingSubscription] = useState(false);

  const [paymentInfo, setPaymentInfo] = useState<BillingData>({
    customerGuid: '',
    customerBillingGuid: '',
    addressBillingCountryCode: '',
    addressBillingState: '',
    addressBillingCity: '',
    addressBillingLine1: '',
    addressBillingLine2: '',
    addressBillingPostalCode: ''
  });

  const countryList = getCountryList(t);

  useEffect(() => {
    const fetchData = async () => {
      if (!user?.customerGuid) return;
      
      setLoading(true);
      setErrors([]); // Clear errors before starting

      const billingPromise = httpClient.get<ApiResponse<BillingData>>(`api/v1/portal/customer-billing/${user.customerGuid}`)
          .then(res => ({ type: 'success' as const, data: res.data }))
          .catch(err => ({ type: 'error' as const, error: err }));

      const plansPromise = httpClient.get<ApiResponse<AvailablePlan[]>>('api/v1/portal/subscription/list-available-plans')
          .then(res => ({ type: 'success' as const, data: res.data }))
          .catch(err => ({ type: 'error' as const, error: err }));

      try {
        const [billingResult, plansResult] = await Promise.all([billingPromise, plansPromise]);
        const newErrors: ApiError[] = [];

        // Handle Billing Response
        if (billingResult.type === 'success') {
           const res = billingResult.data;
           if (res.success && res.data) {
             setPaymentInfo(prev => ({
               ...prev,
               ...res.data!
             }));
           } else if (res.errors) {
             newErrors.push(...res.errors);
           }
        } else {
           const err = billingResult.error;
           if (err.response?.data?.errors) {
             newErrors.push(...err.response.data.errors);
           } else {
             newErrors.push({ propertyName: 'Billing', errorMessage: 'Failed to load billing data', errorCode: 'LOAD_ERROR', statusCode: 500 });
           }
        }

        // Handle Plans Response
        if (plansResult.type === 'success') {
           const res = plansResult.data;
           if (res.success && res.data) {
             if (Array.isArray(res.data) && res.data.length === 0) {
                newErrors.push({
                    propertyName: 'Plans',
                    errorMessage: t('subscription_plans_unavailable_error'),
                    errorCode: 'PLANS_UNAVAILABLE',
                    statusCode: 200
                });
             }
             setAvailablePlans(res.data);
           } else if (res.errors) {
             newErrors.push(...res.errors);
           }
        } else {
           const err = plansResult.error;
           if (err.response?.data?.errors) {
             newErrors.push(...err.response.data.errors);
           } else {
             newErrors.push({ propertyName: 'Plans', errorMessage: 'Failed to load plans data', errorCode: 'LOAD_ERROR', statusCode: 500 });
           }
        }

        if (newErrors.length > 0) {
          setErrors(newErrors);
        }

      } catch (error: any) {
        console.error('Error fetching data:', error);
        setErrors([{ propertyName: 'General', errorMessage: 'Unexpected error loading data', errorCode: 'LOAD_ERROR', statusCode: 500 }]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user?.customerGuid, t]);

  const handleSave = async () => {
    setErrors([]);
    setSuccessMessage('');
    setLoading(true);
    try {
        const payload = { ...paymentInfo };
        const response = await httpClient.put<ApiResponse<BillingData>>('api/v1/portal/customer-billing/insert-or-update-billing-info', payload);
        
        if (response.data.success && response.data.data) {
            setPaymentInfo(prev => ({
              ...prev,
              ...response.data.data!
            }));
            setSuccessMessage(t('subscription_payment_save_success') || 'Saved successfully');
        } else if (response.data.errors) {
            setErrors(response.data.errors);
        }
    } catch (error: any) {
        console.error('Error saving billing data:', error);
         if (error.response?.data?.errors) {
            setErrors(error.response.data.errors);
        } else {
             setErrors([{ propertyName: 'General', errorMessage: 'Failed to save data', errorCode: 'SAVE_ERROR', statusCode: 500 }]);
        }
    } finally {
        setLoading(false);
    }
  };

  const handleChangeTab = (_: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  const handleChangePaymentField = (field: keyof typeof paymentInfo, value: string) => {
    setPaymentInfo(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const planPriceValue = availablePlans.length > 0 && availablePlans[0].priceValueNormalized 
    ? parseFloat(availablePlans[0].priceValueNormalized.replace(/[^0-9.]/g, '')) 
    : 89;

  const plan = availablePlans.length > 0 ? availablePlans[0] : null;
  const activeSubscriptionStatus = useMemo(() => {
    return (plan?.activeSubscription?.status || '').toLowerCase();
  }, [plan?.activeSubscription?.status]);

  const hasActiveSubscription = !!plan?.activeSubscription && (activeSubscriptionStatus === 'active' || activeSubscriptionStatus === 'trialing');

  const paymentFlowStatus = useMemo(() => {
    return (plan?.paymentFlow?.statusCheckout || '').toLowerCase();
  }, [plan?.paymentFlow?.statusCheckout]);

  const isPaymentFlowPending = paymentFlowStatus === 'pending';
  const isPaymentFlowCompleted = paymentFlowStatus === 'completed';
  const isTrialEnabled = !!plan?.trialConfigurations?.enableTrial;
  const shouldBlockPendingCheckout = isPaymentFlowPending && !isTrialEnabled;
  const isTrialingSubscription = !!plan?.activeSubscription && activeSubscriptionStatus === 'trialing';
  const isTrialingWithTrialEnabled = isTrialingSubscription && isTrialEnabled;

  const canCancelSubscription = (hasActiveSubscription && !isTrialingWithTrialEnabled) || isPaymentFlowCompleted;
  const canSubscribe = !canCancelSubscription && !shouldBlockPendingCheckout && !isTrialingWithTrialEnabled;

  const trialEndDateText = useMemo(() => {
    if (!isTrialingWithTrialEnabled) return null;

    const fromApi = plan?.trialConfigurations?.trialEndDateFormatted;
    if (fromApi) return fromApi;

    const days = plan?.trialConfigurations?.trialDays;
    if (!days || days <= 0) return null;

    const endDate = new Date();
    endDate.setDate(endDate.getDate() + days);
    if (Number.isNaN(endDate.getTime())) return null;

    const dd = String(endDate.getDate()).padStart(2, '0');
    const mm = String(endDate.getMonth() + 1).padStart(2, '0');
    const yyyy = String(endDate.getFullYear());
    return `${dd}/${mm}/${yyyy}`;
  }, [isTrialingWithTrialEnabled, plan?.trialConfigurations?.trialEndDateFormatted, plan?.trialConfigurations?.trialDays]);

  const priceFormatted = useMemo(() => {
    if (availablePlans.length > 0 && availablePlans[0].priceValueNormalized) {
        return availablePlans[0].priceValueNormalized;
    }
    return new Intl.NumberFormat(
      i18n.language && i18n.language.startsWith('pt') ? 'pt-BR' : 'en-CA',
      { style: 'currency', currency: 'CAD' }
    ).format(planPriceValue);
  }, [i18n.language, availablePlans, planPriceValue]);

  const paymentFlowInfo = useMemo(() => {
    const paymentFlow = plan?.paymentFlow;
    if (!paymentFlow || paymentFlow.statusCheckout !== 'Completed') return null;

    const locale = i18n.language && i18n.language.startsWith('pt') ? 'pt-BR' : 'en-CA';

    if (!paymentFlow.date) {
      return { isCheckoutCompleted: true, purchaseDateText: null as string | null };
    }

    const parsedDate = new Date(paymentFlow.date);
    if (Number.isNaN(parsedDate.getTime())) {
      return { isCheckoutCompleted: true, purchaseDateText: null as string | null };
    }

    const formattedDate = new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(parsedDate);
    return {
      isCheckoutCompleted: true,
      purchaseDateText: t('subscription_plan_purchase_made_at', { date: formattedDate })
    };
  }, [plan?.paymentFlow, i18n.language, t]);

  const approxPrice = useMemo(() => {
    // Se a API retornar um valor aproximado formatado (priceValueNormalizedAsComplementAprox)
    // e estivermos no locale pt-BR (ou qualquer lógica futura baseada em currencyAsComplement), usamos o valor da API.
    if (availablePlans.length > 0) {
      const plan = availablePlans[0];
      // Verifica se temos o valor complementar e se o idioma atual é compatível (ex: pt-BR)
      // O usuário mencionou: "valores desses campos só serão populados quando é locale em pt-BR"
      if (plan.priceValueNormalizedAsComplementAprox && i18n.language && i18n.language.startsWith('pt')) {
          return plan.priceValueNormalizedAsComplementAprox;
      }
    }
    
    // Fallback para cálculo manual (caso a API não retorne ou não tenhamos dados)
    if (i18n.language && i18n.language.startsWith('pt')) {
      const rate = 4.27;
      const value = Math.round(planPriceValue * rate);
      return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(value);
    }
    return null;
  }, [i18n.language, planPriceValue, availablePlans]);

  const isGeneralUser = user?.userLevel === UserLevel.GENERAL_USER;

  useEffect(() => {
    const fetchPaymentHistory = async () => {
      if (!user?.customerGuid || isGeneralUser) return;

      setPaymentHistoryLoading(true);
      setPaymentHistoryHasError(false);
      try {
        const response = await httpClient.get<ApiResponse<PaymentHistoryItem[]>>('api/v1/portal/subscription/payment-history');

        if (response.data.success && Array.isArray(response.data.data)) {
          setPaymentHistoryItems(response.data.data);
        } else if (response.data.errors) {
          setPaymentHistoryHasError(true);
          setErrors(response.data.errors);
        } else {
          setPaymentHistoryItems([]);
        }
      } catch (error: any) {
        setPaymentHistoryHasError(true);
        if (error.response?.data?.errors) {
          setErrors(error.response.data.errors);
        } else {
          setErrors([{
            propertyName: 'PaymentHistory',
            errorMessage: t('subscription_payment_history_load_error', 'Erro ao carregar histórico de pagamentos.'),
            errorCode: 'LOAD_ERROR',
            statusCode: 500
          }]);
        }
      } finally {
        setPaymentHistoryLoading(false);
      }
    };

    fetchPaymentHistory();
  }, [user?.customerGuid, isGeneralUser, i18n.language, t]);

  const saveButtonText = useMemo(() => {
    // Se customerBillingGuid for null/vazio E addressBillingCountryCode estiver preenchido
    if (!paymentInfo.customerBillingGuid && paymentInfo.addressBillingCountryCode) {
      return t('subscription_payment_confirm_button');
    }
    return t('subscription_payment_save_button');
  }, [paymentInfo.customerBillingGuid, paymentInfo.addressBillingCountryCode, t]);

  const handleSubscribe = async () => {
    setSubscribing(true);
    setErrors([]);
    setWarningMessage(null);

    const payload: CheckoutPayload = {
      priceId: availablePlans.length > 0 ? availablePlans[0].priceId : ''
    };

    try {
      const response = await httpClient.post<ApiResponse<CheckoutResponse>>('api/v1/portal/subscription/checkout', payload);
      
      if (response.data.success && response.data.data) {
        const { shouldRedirectToEnrollment, isSuccesToRedirectToCheckout, urlRedirectCheckout } = response.data.data;

        if (shouldRedirectToEnrollment && !isSuccesToRedirectToCheckout) {
          setWarningMessage({
            text: t('subscription_plan_enrollment_warning_billing', 'É necessário preencher os dados de cobrança antes de assinar.'),
            linkText: t('subscription_plan_enrollment_link_billing', 'Ir para Endereço de Cobrança'),
            action: () => setTabIndex(0) // Assuming 0 is the index for "Endereço de Cobrança" when user is not general user
          });
        } else if (!shouldRedirectToEnrollment && isSuccesToRedirectToCheckout && urlRedirectCheckout) {
          window.location.href = urlRedirectCheckout;
        }
      } else if (response.data.errors) {
        setErrors(response.data.errors);
      }
    } catch (error: any) {
      console.error('Error subscribing:', error);
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        setErrors([{ 
          propertyName: 'Checkout', 
          errorMessage: t('subscription_plan_checkout_error', 'Erro ao processar assinatura.'), 
          errorCode: 'CHECKOUT_ERROR', 
          statusCode: 500 
        }]);
      }
    } finally {
      setSubscribing(false);
    }
  };

  const reloadPlans = async () => {
    try {
      const res = await httpClient.get<ApiResponse<AvailablePlan[]>>('api/v1/portal/subscription/list-available-plans');
      if (res.data.success && Array.isArray(res.data.data)) {
        setAvailablePlans(res.data.data);
      } else if (res.data.errors) {
        setErrors(res.data.errors);
      }
    } catch (error: any) {
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        setErrors([{
          propertyName: 'Plans',
          errorMessage: t('subscription_plans_unavailable_error'),
          errorCode: 'LOAD_ERROR',
          statusCode: 500
        }]);
      }
    }
  };

  const handleOpenCancelDialog = () => {
    setCancelDialogOpen(true);
  };

  const handleCloseCancelDialog = () => {
    if (cancelingSubscription) return;
    setCancelDialogOpen(false);
  };

  const handleConfirmCancelSubscription = async () => {
    setCancelingSubscription(true);
    setErrors([]);
    try {
      const res = await httpClient.post<ApiResponse<boolean>>('api/v1/portal/subscription/cancel');
      if (res.data.success && res.data.data === true) {
        setSuccessMessage(t('subscription_cancel_success'));
        setCancelDialogOpen(false);
        await reloadPlans();
      } else if (res.data.errors) {
        setErrors(res.data.errors);
      } else {
        setErrors([{
          propertyName: 'CancelSubscription',
          errorMessage: t('subscription_cancel_error'),
          errorCode: 'CANCEL_ERROR',
          statusCode: 400
        }]);
      }
    } catch (error: any) {
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        setErrors([{
          propertyName: 'CancelSubscription',
          errorMessage: t('subscription_cancel_error'),
          errorCode: 'CANCEL_ERROR',
          statusCode: 500
        }]);
      }
    } finally {
      setCancelingSubscription(false);
    }
  };

  const formatDateForHistory = (dateStr?: string | null) => {
    if (!dateStr) return '-';
    const parsed = new Date(dateStr);
    if (Number.isNaN(parsed.getTime())) return '-';
    const locale = i18n.language && i18n.language.startsWith('pt') ? 'pt-BR' : 'en-CA';
    return new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(parsed);
  };

  const resolveStatusLabel = (status: string) => {
    const normalized = (status || '').toLowerCase();
    switch (normalized) {
      case 'paid':
        return t('subscription_payment_status_paid');
      case 'open':
        return t('subscription_payment_status_open');
      case 'draft':
        return t('subscription_payment_status_draft');
      case 'uncollectible':
        return t('subscription_payment_status_uncollectible');
      case 'void':
        return t('subscription_payment_status_void');
      default:
        return status || '-';
    }
  };

  const handleDownloadInvoice = async (item: PaymentHistoryItem) => {
    if (!item.invoiceDownloadUrl) {
      setErrors([{
        propertyName: 'InvoiceDownload',
        errorMessage: t('subscription_payment_history_invoice_unavailable', 'Nota indisponível para download.'),
        errorCode: 'INVOICE_UNAVAILABLE',
        statusCode: 400
      }]);
      return;
    }

    setInvoiceDownloadingId(item.invoiceIdStripe);
    try {
      const response = await fetch(item.invoiceDownloadUrl);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      const safeInvoiceNumber = (item.invoiceNumber || item.invoiceIdStripe || 'invoice').replace(/[^a-zA-Z0-9-_]/g, '_');
      link.download = `invoice-${safeInvoiceNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);
    } catch {
      const opened = window.open(item.invoiceDownloadUrl, '_blank', 'noopener,noreferrer');
      if (!opened) {
        setErrors([{
          propertyName: 'InvoiceDownload',
          errorMessage: t('subscription_payment_history_download_error', 'Erro ao baixar a nota.'),
          errorCode: 'DOWNLOAD_ERROR',
          statusCode: 500
        }]);
      }
    } finally {
      setInvoiceDownloadingId(null);
    }
  };

  const handleInvoiceAction = async (item: PaymentHistoryItem) => {
    if (item.invoicePayUrl) {
      window.location.href = item.invoicePayUrl;
      return;
    }

    await handleDownloadInvoice(item);
  };

  return (
    <MainLayout>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Paper elevation={2} sx={{ p: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3 }}>
            {t('subscription_management_title')}
          </Typography>

          {errors.length > 0 && (
            <Box sx={{ mb: 3 }}>
              {errors.map((error, index) => (
                <Alert severity="error" key={index} sx={{ mb: 1 }}>
                  {error.errorMessage}
                </Alert>
              ))}
            </Box>
          )}

          <Snackbar
            open={!!successMessage}
            autoHideDuration={6000}
            onClose={() => setSuccessMessage('')}
          >
            <Alert onClose={() => setSuccessMessage('')} severity="success" sx={{ width: '100%' }}>
              {successMessage}
            </Alert>
          </Snackbar>

          <Grid container spacing={3} sx={{ flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
            <Grid component={'div' as React.ElementType} item xs={12} md={3}>
              <Tabs
                orientation="vertical"
                variant="scrollable"
                value={tabIndex}
                onChange={handleChangeTab}
                sx={{ borderRight: 1, borderColor: 'divider' }}
              >
                {!isGeneralUser && <Tab label={t('subscription_tabs_payment_info')} />}
                <Tab label={t('subscription_tabs_plan')} />
                {!isGeneralUser && <Tab label={t('subscription_tabs_history')} />}
              </Tabs>
            </Grid>

            <Grid component={'div' as React.ElementType} item xs={12} md={9} sx={{ minWidth: 0 }}>
              {!isGeneralUser && tabIndex === 0 && (
                <Box>
                  <Typography variant="subtitle1" sx={{ mb: 2 }}>
                    {t('subscription_tabs_payment_info')}
                  </Typography>
                  <Paper sx={{ p: 2, maxWidth: 900 }}>
                      <Grid container spacing={2}>
                        
                      </Grid>
                    <Grid container spacing={2}>
                      <Grid component={'div' as React.ElementType} item xs={12} md={4}>
                        <FormControl fullWidth sx={{ maxWidth: 260 }}>
                          <InputLabel id="billingCountryLabel">{t('subscription_payment_country_code')}</InputLabel>
                          <Select
                            labelId="billingCountryLabel"
                            id="billingCountry"
                            value={paymentInfo.addressBillingCountryCode}
                            label={t('subscription_payment_country_code')}
                            onChange={(e) => handleChangePaymentField('addressBillingCountryCode', e.target.value)}
                            renderValue={(val) => {
                              const c = countryList.find(x => x.code === val);
                              const displayText = c?.name || String(val ?? '');
                              return (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, maxWidth: '100%' }}>
                                  <span className={c?.className || ''} />
                                  <Typography
                                    variant="body2"
                                    sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                                  >
                                    {displayText}
                                  </Typography>
                                </Box>
                              );
                            }}
                            MenuProps={{ disablePortal: true }}
                          >
                            {countryList.map((c) => (
                              <MenuItem key={c.code} value={c.code}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <span className={c.className} />
                                  <Typography variant="body2">{c.name}</Typography>
                                </Box>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <br />
                           <br />
                      </Grid>

                    </Grid>
                    <Grid container spacing={2}>
                      <Grid component={'div' as React.ElementType} item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label={t('subscription_payment_state')}
                          value={paymentInfo.addressBillingState}
                          onChange={(e) => handleChangePaymentField('addressBillingState', e.target.value)}
                        />
                      </Grid>
                      <Grid component={'div' as React.ElementType} item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label={t('subscription_payment_city')}
                          value={paymentInfo.addressBillingCity}
                          onChange={(e) => handleChangePaymentField('addressBillingCity', e.target.value)}
                        />
                      </Grid>
                      <Grid component={'div' as React.ElementType} item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label={t('subscription_payment_address_line1')}
                          value={paymentInfo.addressBillingLine1}
                          onChange={(e) => handleChangePaymentField('addressBillingLine1', e.target.value)}
                        />
                      </Grid>
                      <Grid component={'div' as React.ElementType} item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label={t('subscription_payment_address_line2')}
                          value={paymentInfo.addressBillingLine2}
                          onChange={(e) => handleChangePaymentField('addressBillingLine2', e.target.value)}
                        />
                      </Grid>
                      <Grid component={'div' as React.ElementType} item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label={t('subscription_payment_postal_code')}
                          value={paymentInfo.addressBillingPostalCode}
                          onChange={(e) => handleChangePaymentField('addressBillingPostalCode', e.target.value)}
                        />
                      </Grid>
                    </Grid>
                    <Box sx={{ mt: 3 }}>
                      <Button variant="contained" onClick={handleSave} disabled={loading}>
                        {loading ? <CircularProgress size={24} color="inherit" /> : saveButtonText}
                      </Button>
                    </Box>
                  </Paper>
                </Box>
              )}

              {((!isGeneralUser && tabIndex === 1) || (isGeneralUser && tabIndex === 0)) && (
                <Box>
                  {warningMessage && (
                      <Alert severity="warning" sx={{ mb: 2 }}>
                        {warningMessage.text}
                        {warningMessage.link && (
                          <Button 
                            size="small"
                            onClick={() => navigate(warningMessage.link!)}
                            sx={{ display: 'block', mt: 1, textAlign: 'left', p: 0, minWidth: 0, textTransform: 'none', textDecoration: 'underline' }}
                          >
                            {warningMessage.linkText}
                          </Button>
                        )}
                        {warningMessage.action && (
                          <Button 
                            size="small"
                            onClick={warningMessage.action}
                            sx={{ display: 'block', mt: 1, textAlign: 'left', p: 0, minWidth: 0, textTransform: 'none', textDecoration: 'underline' }}
                          >
                            {warningMessage.linkText}
                          </Button>
                        )}
                      </Alert>
                    )}

                  <Typography variant="h6" sx={{ mb: 2 }}>
                    {t('subscription_tabs_plan')}
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    {plan ? (
                      <Card sx={{ maxWidth: 420, width: '100%', position: 'relative', overflow: 'visible' }}>
                        {plan.isProductionPlan === false && (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 14,
                              right: 14,
                              px: 1.5,
                              py: 0.5,
                              bgcolor: '#ff9800',
                              color: '#fff',
                              borderRadius: 1,
                              fontSize: '0.65rem',
                              fontWeight: 'bold',
                            }}
                          >
                            Sandbox
                          </Box>
                        )}
                        <CardContent>
                          <Typography variant="h6" sx={{ mb: 1 }}>
                            {plan.commercialName}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {plan.description}
                          </Typography>
                          {canSubscribe && plan.trialConfigurations?.enableTrial && (plan.trialConfigurations.trialDays ?? 0) > 0 && (
                              <Typography variant="body2" color="success.main" sx={{ mb: 2, fontWeight: 600 }}>
                                {t('subscription_plan_trial_days_label', { days: plan.trialConfigurations.trialDays })}
                              </Typography>
                            )}
                          {paymentFlowInfo?.purchaseDateText && (
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                              {paymentFlowInfo.purchaseDateText}
                            </Typography>
                          )}
                          {(canSubscribe || shouldBlockPendingCheckout) && (
                            <>
                              <Typography variant="h5" sx={{ mb: 3 }}>
                                {priceFormatted}
                              </Typography>
                              {approxPrice && (
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2, fontStyle: 'italic' }}>
                                  {t('subscription_plan_approximate_conversion_text')} {approxPrice}
                                </Typography>
                              )}
                            </>
                          )}
                          {shouldBlockPendingCheckout && (
                            <Button
                              variant="contained"
                              fullWidth
                              disabled
                              sx={{
                                '&.Mui-disabled': {
                                  bgcolor: 'grey.400',
                                  color: 'grey.800'
                                }
                              }}
                            >
                              {t('subscription_plan_awaiting_processing_button')}
                            </Button>
                          )}
                          {isTrialingWithTrialEnabled && (
                            <>
                              <Button
                                variant="contained"
                                fullWidth
                                disabled
                                sx={{
                                  '&.Mui-disabled': {
                                    bgcolor: 'grey.400',
                                    color: 'grey.800'
                                  }
                                }}
                              >
                                {t('subscription_plan_trial_enabled_button')}
                              </Button>
                              {trialEndDateText && (
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                                  {t('subscription_plan_trial_expires_at', { date: trialEndDateText })}
                                </Typography>
                              )}
                            </>
                          )}
                          {canSubscribe && (
                            <>
                              <Button
                                variant="contained"
                                fullWidth
                                onClick={handleSubscribe}
                                disabled={subscribing}
                              >
                                {subscribing ? (
                                  <CircularProgress size={24} color="inherit" />
                                ) : (
                                  t('subscription_plan_card_subscribe_button')
                                )}
                              </Button>
                            </>
                          )}
                          {canCancelSubscription && (
                            <Button
                              variant="contained"
                              color="error"
                              fullWidth
                              onClick={handleOpenCancelDialog}
                              disabled={cancelingSubscription}
                            >
                              {cancelingSubscription ? (
                                <CircularProgress size={24} color="inherit" />
                              ) : (
                                t('subscription_cancel_button')
                              )}
                            </Button>
                          )}
                        </CardContent>
                      </Card>
                    ) : (
                      <Alert severity="error" sx={{ width: '100%', maxWidth: 420 }}>
                        {t('subscription_plans_unavailable_tab', 'Indisponível no momento.')}
                      </Alert>
                    )}
                  </Box>
                </Box>
              )}

              {!isGeneralUser && tabIndex === 2 && (
                <Box>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    {t('subscription_tabs_history')}
                  </Typography>
                  {paymentHistoryLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                      <CircularProgress />
                    </Box>
                  ) : paymentHistoryItems.length === 0 && !paymentHistoryHasError ? (
                    <Alert severity="info">
                      {t('subscription_payment_history_empty')}
                    </Alert>
                  ) : (
                    <Paper sx={{ width: '100%', overflowX: 'auto' }}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>{t('subscription_payment_history_due_date')}</TableCell>
                            <TableCell>{t('subscription_payment_history_payment_date')}</TableCell>
                            <TableCell>{t('subscription_payment_history_status')}</TableCell>
                            <TableCell>{t('subscription_payment_history_plan')}</TableCell>
                            <TableCell>{t('subscription_payment_history_invoice_action')}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {paymentHistoryItems.map((item) => (
                            <TableRow key={item.invoiceIdStripe}>
                              <TableCell>{formatDateForHistory(item.dueDate)}</TableCell>
                              <TableCell>{formatDateForHistory(item.paymentDate)}</TableCell>
                              <TableCell>{resolveStatusLabel(item.status)}</TableCell>
                              <TableCell>{item.plan}</TableCell>
                              <TableCell>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() => handleInvoiceAction(item)}
                                  disabled={(!item.invoicePayUrl && !item.invoiceDownloadUrl) || invoiceDownloadingId === item.invoiceIdStripe}
                                >
                                  {invoiceDownloadingId === item.invoiceIdStripe ? (
                                    <CircularProgress size={18} color="inherit" />
                                  ) : (
                                    item.invoicePayUrl
                                      ? t('subscription_payment_history_pay_button')
                                      : t('subscription_payment_history_download_button')
                                  )}
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Paper>
                  )}
                </Box>
              )}
            </Grid>
          </Grid>
        </Paper>
      </Container>
      <Dialog open={cancelDialogOpen} onClose={handleCloseCancelDialog} fullWidth maxWidth="xs">
        <DialogTitle>{t('subscription_cancel_dialog_title')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {t('subscription_cancel_dialog_message')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCancelDialog} disabled={cancelingSubscription}>
            {t('subscription_cancel_dialog_exit')}
          </Button>
          <Button onClick={handleConfirmCancelSubscription} variant="contained" color="error" disabled={cancelingSubscription}>
            {cancelingSubscription ? <CircularProgress size={18} color="inherit" /> : t('subscription_cancel_dialog_confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </MainLayout>
  );
};

export default SubscriptionPlan;
